import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    ApiPortalBackend,
    PortalWalletBalance,
    PortalWalletTransaction,
    PortalPlansCatalog,
    PortalSubscription,
    PortalSubscriptionPlan,
} from '../../apis/ApiPortalBackend';
import { WALLET_REFRESH_EVENT, requestWalletRefresh } from '../../utils/walletRefresh';
import sengiCoin from '../../assets/sengi_coin_yellow.png';

// Schmaler Übersetzer-Typ, den die modulglobalen Label-Helfer annehmen (i18n, Task #76). Die
// Komponente reicht einen Wrapper um react-i18nexts t durch, damit die Label-Logik hier bleiben
// kann statt in die JSX zu wandern. Fallback über { defaultValue }.
type Translate = (key: string, opts?: Record<string, unknown>) => string;

// Fallback-USt.-Satz, falls PortalBackend die neuen gross_/vat_-Felder (noch) nicht durchreicht.
// Seit 2026-07-22 liefert der PaymentService-Katalog die Brutto-/USt.-Werte laenderabhaengig selbst
// (GET /plans, siehe LoginWithSengiPaymentService/API.md) - priceView() bevorzugt diese und nutzt
// VAT_RATE nur, wenn sie fehlen. Kein lokales Land-Raten (Preislogik bleibt server-seitig).
const VAT_RATE = 0.19;

const formatNumber = (num: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(num);
const formatEur = (eur: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(eur);

// Vereinheitlicht Netto/Brutto/USt. fuer die Anzeige: bevorzugt die server-gelieferten Werte,
// faellt sonst auf Netto x (1+VAT_RATE) zurueck (bisheriges Verhalten, kein Bruch). Bei
// vat_applicable === false (Nicht-EU) ist brutto == netto.
interface PriceView { net: number; gross: number; rate: number; applicable: boolean; }
const priceView = (net: number, gross?: number, vatRate?: number, vatApplicable?: boolean): PriceView => {
    const applicable = vatApplicable ?? true;
    const rate = vatRate ?? VAT_RATE;
    const grossAmount = gross ?? (applicable ? net * (1 + rate) : net);
    return { net, gross: grossAmount, rate, applicable };
};
// Kurzer USt.-Zusatz fuer Katalog-Kacheln (i18n via billing:vat.*).
const vatSuffix = (view: PriceView, tr: Translate) =>
    view.applicable ? tr('billing:vat.incl', { rate: Math.round(view.rate * 100) }) : tr('billing:vat.excl');
const formatDate = (value: string | number | Date) => {
    const date = value instanceof Date ? value : typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

// PortalBackend schreibt fuer Run-Verbrauch die description als "${RunType} run" (siehe
// debitRunUsage-Aufruf in SengiLabsPortalBackend/RunRoutesV1.ts) - das ist die einzige Stelle,
// an der die Kategorie eines Verbrauchs-Eintrags steckt, es gibt kein eigenes Feld dafuer.
// Uebersetzung ueber billing:runType.<RunType> (Fallback: Rohwert). Neuer RunType -> Key in
// locales/*/billing.json ergaenzen (synchron mit dem RunType-Union in ApiPortalBackend.tsx).
const categoryLabel = (tx: PortalWalletTransaction, tr: Translate): string => {
    const raw = tx.description?.trim().replace(/\s+run$/i, '');
    if (!raw) return tx.reference || tr('billing:other');
    return tr(`billing:runType.${raw}`, { defaultValue: raw });
};

// PaymentService-Beschreibungen fuer Einzahlungen enthalten oft interne Details, die fuer
// Nutzer nichtssagend/zu technisch sind, z.B. "Stripe prepaid purchase
// (checkout.session.completed) cs_test_a12fEL4WKNOnVHiZYOO8QyjfQeRW8Bo5Q5RNd8kI13Qk4vWoyIDWjjKapx"
// oder "Stripe subscription, period starting 2026-07-16T00:18:07.000Z" (live auf Stage
// gesehen, 2026-07-16) - Klammer-Zusaetze, lange ID-Token und ISO-Zeitstempel rausfiltern,
// verwaiste Interpunktion danach aufraeumen, Rest behalten.
const cleanDepositDescription = (description?: string): string => {
    if (!description) return '';
    return description
        .replace(/\([^)]*\)/g, ' ')
        .replace(/\b[\w-]{16,}\b/g, ' ')
        .replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z?/g, ' ')
        .replace(/\s+([,.])/g, '$1')
        .replace(/[,.]\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
};
// Zusatzinfo hinter "Einzahlung" in der kumulierten Ansicht - nur wenn die bereinigte
// Beschreibung etwas Eigenes beitraegt (nicht nur das ohnehin schon angezeigte Typ-Label wiederholt).
const depositDetail = (tx: PortalWalletTransaction, tr: Translate): string | null => {
    const cleaned = cleanDepositDescription(tx.description);
    if (!cleaned || cleaned === tr(`billing:txType.${tx.type}`, { defaultValue: '' })) return null;
    return cleaned;
};

interface UsageBlock {
    kind: 'usage';
    key: string;
    label: string;
    categories: { name: string; count: number; total: number }[];
    totalSum: number;
    endingBalance: number | null;
}
interface DepositBlock {
    kind: 'deposit';
    key: string;
    label: string;
    items: { key: string; label: string; amount: number }[];
    totalSum: number;
    endingBalance: number;
}
type CumulativeBlock = UsageBlock | DepositBlock;

// Item-Zeile ohne Datum - das steht wie bei den Verbrauchs-Bloecken bereits in der
// Block-Überschrift (siehe depositBlockLabel), nicht mehr pro Zeile wiederholt.
const depositItemLabel = (tx: PortalWalletTransaction, tr: Translate): string => {
    const detail = depositDetail(tx, tr);
    return `${tr('billing:transactions.deposit')}${detail ? ` · ${detail}` : ''}`;
};

// Ueberschrift eines Einzahlungs-Blocks: einzelnes Datum, oder Zeitspanne, falls mehrere
// gruppierte Einzahlungen an unterschiedlichen Tagen erfolgten - items sind chronologisch
// aufsteigend sortiert (siehe buildCumulativeBlocks).
const depositBlockLabel = (items: PortalWalletTransaction[]): string => {
    const first = formatDate(items[0].createdAt);
    const last = formatDate(items[items.length - 1].createdAt);
    return first === last ? first : `${first} – ${last}`;
};

// Baut eine chronologische Blockliste: Verbrauch (type "usage") wird pro Zeitraum nach
// Kategorie (RunType) aufsummiert, "Einzahlungen" (alles außer "usage") werden als eigener
// Block genauso strukturiert dargestellt (Einzelposten + Gesamt/Kontostand) - auf
// Nutzerwunsch (2026-07-15) "ähnlich wie die Bezahlungen", nicht nur als Randnotiz. Mehrere
// unmittelbar aufeinanderfolgende Einzahlungen (kein Verbrauch dazwischen - "Zwischenblöcke")
// werden zu einem gemeinsamen Block gruppiert, genau wie mehrere Kategorien in einem
// Verbrauchs-Zeitraum. Der letzte, noch offene Verbrauchs-Zeitraum bekommt "seit {Datum}" statt
// "{von} - {bis}".
const buildCumulativeBlocks = (transactions: PortalWalletTransaction[], tr: Translate): CumulativeBlock[] => {
    const chronological = [...transactions].reverse(); // API liefert newest-first
    const blocks: CumulativeBlock[] = [];

    let usageFrom: Date | null = null;
    let usageCategories = new Map<string, { count: number; total: number }>();
    let usageEndingBalance: number | null = null;
    let depositItems: PortalWalletTransaction[] = [];

    const flushUsage = (toDate: Date | null) => {
        if (usageCategories.size === 0) return;
        const label = toDate
            ? (usageFrom
                ? tr('billing:transactions.period.range', { from: formatDate(usageFrom), to: formatDate(toDate) })
                : tr('billing:transactions.period.until', { date: formatDate(toDate) }))
            : (usageFrom
                ? tr('billing:transactions.period.since', { date: formatDate(usageFrom) })
                : tr('billing:transactions.period.all'));
        const categories = Array.from(usageCategories.entries())
            .map(([name, v]) => ({ name, count: v.count, total: v.total }))
            .sort((a, b) => b.total - a.total);
        blocks.push({
            kind: 'usage',
            key: `usage-${blocks.length}-${toDate?.toISOString() ?? 'open'}`,
            label,
            categories,
            totalSum: categories.reduce((sum, c) => sum + c.total, 0),
            endingBalance: usageEndingBalance,
        });
        usageCategories = new Map();
        usageEndingBalance = null;
    };

    const flushDeposits = () => {
        if (depositItems.length === 0) return;
        blocks.push({
            kind: 'deposit',
            key: `deposit-${depositItems[0].id}`,
            label: depositBlockLabel(depositItems),
            items: depositItems.map(tx => ({ key: `${tx.id}`, label: depositItemLabel(tx, tr), amount: tx.delta })),
            totalSum: depositItems.reduce((sum, tx) => sum + tx.delta, 0),
            endingBalance: depositItems[depositItems.length - 1].balanceAfter,
        });
        depositItems = [];
    };

    for (const tx of chronological) {
        if (tx.type !== 'usage') {
            flushUsage(new Date(tx.createdAt));
            usageFrom = new Date(tx.createdAt);
            depositItems.push(tx);
            continue;
        }
        flushDeposits();
        const name = categoryLabel(tx, tr);
        const bucket = usageCategories.get(name) ?? { count: 0, total: 0 };
        bucket.count += 1;
        bucket.total += Math.abs(tx.delta);
        usageCategories.set(name, bucket);
        usageEndingBalance = tx.balanceAfter;
    }
    flushDeposits();
    flushUsage(null);

    return blocks.reverse(); // neuester Block zuerst, konsistent mit der linearen Ansicht
};

type CheckoutSelection =
    | { kind: 'subscription'; plan: PortalSubscriptionPlan }
    // amountEur bleibt der Netto-Preispunkt (so erwartet PaymentService ihn beim Checkout);
    // grossEur/vatRate/vatApplicable nur fuer die Anzeige, optional (Fallback via priceView).
    | { kind: 'prepaid'; amountEur: number; credits: number; grossEur?: number; vatRate?: number; vatApplicable?: boolean };

// Checkout confirmation implementing the § 312j BGB structure (unambiguous order button label,
// total price incl. VAT, subscription terms) and the § 356 Abs. 5 BGB withdrawal-waiver checkbox
// for digital content. Wording is placeholder until the legal review (Task #35) delivers final
// texts - the STRUCTURE (which checkboxes are mandatory before the order button unlocks) is the
// legally required part and must not be weakened.
const CheckoutConfirm = ({
    selection,
    onCancel,
    onError,
}: {
    selection: CheckoutSelection;
    onCancel: () => void;
    onError: (message: string) => void;
}) => {
    const { t } = useTranslation(['billing', 'common']);
    const [waiverAccepted, setWaiverAccepted] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    // PayPal vorerst deaktiviert (Nutzer-Entscheidung 2026-07-15): PayPal berechnet die USt.
    // nicht automatisch, Stripe (Tax) schon - bis das geklaert ist, laeuft Checkout nur ueber
    // Stripe. Der PayPal-Capture-Flow auf /payment/success bleibt fuer eine Reaktivierung stehen.
    const paymentMethod = 'card' as const;
    const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
    const [submitting, setSubmitting] = useState(false);

    const isSubscription = selection.kind === 'subscription';
    const priceInfo = isSubscription
        ? priceView(
            billingInterval === 'yearly' ? selection.plan.amount_yearly_eur : selection.plan.amount_monthly_eur,
            billingInterval === 'yearly' ? selection.plan.gross_amount_yearly_eur : selection.plan.gross_amount_monthly_eur,
            selection.plan.vat_rate, selection.plan.vat_applicable)
        : priceView(selection.amountEur, selection.grossEur, selection.vatRate, selection.vatApplicable);

    const startCheckout = async () => {
        setSubmitting(true);
        const result = await ApiPortalBackend.CreateCheckoutSession(
            isSubscription
                ? { payment_type: 'subscription', payment_method: paymentMethod, plan: selection.plan.id, interval: billingInterval }
                : { payment_type: 'prepaid', payment_method: paymentMethod, amount_eur: selection.amountEur }
        );
        if (result?.success && result.checkout_url) {
            window.location.href = result.checkout_url;
            return;
        }
        setSubmitting(false);
        onError(result?.error || t('billing:checkout.startFailed'));
    };

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center billing-checkout-overlay"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
            onClick={onCancel}
        >
            <div className="sengi-dialog text-body p-4" style={{ maxWidth: 480, width: '90%' }} onClick={e => e.stopPropagation()}>
                <h5 className="mb-3">{t('billing:checkout.title')}</h5>

                {isSubscription && (
                    <div className="mb-2">
                        <div className="form-check form-check-inline">
                            <input className="form-check-input" type="radio" name="interval" id="iv-monthly"
                                checked={billingInterval === 'monthly'} onChange={() => setBillingInterval('monthly')} />
                            <label className="form-check-label" htmlFor="iv-monthly">
                                {t('billing:checkout.intervalMonthly', { price: formatEur(selection.plan.amount_monthly_eur) })}
                            </label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input className="form-check-input" type="radio" name="interval" id="iv-yearly"
                                checked={billingInterval === 'yearly'} onChange={() => setBillingInterval('yearly')} />
                            <label className="form-check-label" htmlFor="iv-yearly">
                                {t('billing:checkout.intervalYearly', { price: formatEur(selection.plan.amount_yearly_eur) })}
                            </label>
                        </div>
                    </div>
                )}

                {/* § 312j: Gesamtpreis (brutto) direkt vor dem Bestellbutton. Volle Aufschlüsselung
                    Netto/USt./Brutto - Werte aus dem PaymentService-Katalog (priceInfo), Fallback
                    Netto × (1+VAT_RATE), falls die Brutto-Felder fehlen.
                    TODO(#35): rechtsverbindliche EN-Formulierungen (Bestellbutton/Widerrufsverzicht/
                    Preisangaben) mit der anwaltlichen Prüfung final abgleichen - aktuell aus
                    billing:checkout.* (Nutzer-freigegebene Übersetzung, Art. 16(m) CRD-Sprache). */}
                <div className="d-flex justify-content-between">
                    <span>
                        {isSubscription
                            ? t('billing:checkout.lineSubscription', {
                                name: selection.plan.name,
                                interval: t(billingInterval === 'yearly' ? 'billing:checkout.intervalWordYearly' : 'billing:checkout.intervalWordMonthly'),
                            })
                            : t('billing:checkout.linePrepaid', { n: formatNumber(selection.credits) })}
                    </span>
                    <span className="num">{formatEur(priceInfo.net)}</span>
                </div>
                {priceInfo.applicable && (
                    <div className="d-flex justify-content-between small text-secondary">
                        <span>{t('billing:checkout.vatLine', { rate: Math.round(priceInfo.rate * 100) })}</span>
                        <span className="num">{formatEur(priceInfo.gross - priceInfo.net)}</span>
                    </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between mb-1">
                    <span className="fw-bold">{priceInfo.applicable ? t('billing:checkout.totalInclVat') : t('billing:checkout.total')}</span>
                    <span className="fw-bold num">{formatEur(priceInfo.gross)}</span>
                </div>
                {!priceInfo.applicable && (
                    <div className="small text-secondary">{t('billing:checkout.exclNote')}</div>
                )}
                <div className="small text-secondary mt-1">
                    {t('billing:checkout.stripeTaxHint')}
                </div>
                <div className="small text-secondary mb-3">
                    {isSubscription
                        ? t(billingInterval === 'yearly' ? 'billing:checkout.subTermsYearly' : 'billing:checkout.subTermsMonthly', { n: formatNumber(selection.plan.coins) })
                        : t('billing:checkout.prepaidTerms', { n: formatNumber(selection.credits) })}
                </div>

                <div className="small text-secondary mb-3">
                    {t('billing:checkout.stripeInfo')}
                </div>

                <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="chk-waiver"
                        checked={waiverAccepted} onChange={e => setWaiverAccepted(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="chk-waiver">
                        {t('billing:checkout.waiver')}
                    </label>
                </div>
                <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="chk-terms"
                        checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="chk-terms">
                        {/* TODO(#35): Link auf die finale Widerrufsbelehrung (aktuell beide auf /terms) */}
                        <Trans
                            t={t}
                            i18nKey="billing:checkout.termsAccept"
                            components={[
                                <a href="/terms" target="_blank" rel="noreferrer" />,
                                <a href="/terms" target="_blank" rel="noreferrer" />,
                            ]}
                        />
                    </label>
                </div>

                <div className="d-flex gap-2 justify-content-end">
                    <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={submitting}>
                        {t('billing:checkout.cancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sengi"
                        onClick={startCheckout}
                        disabled={!waiverAccepted || !termsAccepted || submitting}
                    >
                        {submitting ? t('billing:checkout.submitting') : t('billing:checkout.orderButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const BillingPage = () => {
    const { t } = useTranslation(['billing', 'common']);
    // Wrapper mit schmalem Typ fuer die modulglobalen Label-Helfer (categoryLabel, vatSuffix,
    // buildCumulativeBlocks). t selbst hat einen komplexeren Rueckgabetyp, daher hier vereinheitlicht.
    const tr: Translate = (key, opts) => t(key, opts) as string;
    const [balance, setBalance] = useState<PortalWalletBalance | null>(null);
    const [plans, setPlans] = useState<PortalPlansCatalog | null>(null);
    const [subscription, setSubscription] = useState<PortalSubscription | null>(null);
    const [transactions, setTransactions] = useState<PortalWalletTransaction[] | null>(null);
    const [loadFailed, setLoadFailed] = useState<{ wallet: boolean; plans: boolean; transactions: boolean }>({ wallet: false, plans: false, transactions: false });
    const [selection, setSelection] = useState<CheckoutSelection | null>(null);
    const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [transactionsView, setTransactionsView] = useState<'linear' | 'cumulative'>('cumulative');

    const loadAll = useCallback(async () => {
        const [walletRes, plansRes, subRes, txRes] = await Promise.all([
            ApiPortalBackend.GetWallet(),
            ApiPortalBackend.GetPlans(),
            ApiPortalBackend.GetSubscription(),
            ApiPortalBackend.GetTransactions(50, 0),
        ]);
        if (walletRes) setBalance(walletRes.balance);
        if (plansRes) setPlans(plansRes);
        if (subRes) setSubscription(subRes.subscription ?? null);
        if (txRes) setTransactions(txRes.transactions);
        setLoadFailed({ wallet: !walletRes, plans: !plansRes, transactions: !txRes });
    }, []);

    useEffect(() => {
        document.title = 'Billing';
        loadAll();
        window.addEventListener(WALLET_REFRESH_EVENT, loadAll);
        return () => window.removeEventListener(WALLET_REFRESH_EVENT, loadAll);
    }, [loadAll]);

    const cancelSubscription = async () => {
        if (!window.confirm(t('billing:subscription.cancelConfirm'))) return;
        setCancelling(true);
        const result = await ApiPortalBackend.CancelSubscription();
        setCancelling(false);
        if (result?.success) {
            const when = result.cancel_at ? t('billing:subscription.cancelWhen', { date: formatDate(result.cancel_at) }) : '';
            setMessage({ kind: 'success', text: t('billing:subscription.cancelSuccess', { when }) });
            requestWalletRefresh();
        } else {
            setMessage({ kind: 'danger', text: result?.error || t('billing:subscription.cancelFailed') });
        }
    };

    const total = balance?.total ?? 0;
    const meterSegment = (part: number) => total > 0 ? { width: `${(part / total) * 100}%` } : { width: '0%' };

    return (
        <div className="container py-4 overflow-auto h-100 billing-page">
            <h2>{t('billing:title')}</h2>
            <p className="billing-subtitle">{t('billing:subtitle')}</p>

            {message && (
                <div className={`alert alert-${message.kind} alert-dismissible`}>
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                </div>
            )}

            {/* Kontostand + Abo nebeneinander */}
            <div className="row g-3">
                <div className="col-lg-7">
                    <div className="billing-card p-4 h-100">
                        <div className="d-flex align-items-center gap-3">
                            <div className="billing-coin"><img src={sengiCoin} alt="" /></div>
                            <div className="flex-grow-1">
                                <div className="billing-label mb-1">{t('billing:balance.label')}</div>
                                {balance ? (
                                    <>
                                        <div className="billing-balance">
                                            {formatNumber(balance.total)}
                                            <span className="billing-balance-unit">{t('billing:balance.unit')}</span>
                                        </div>
                                        <div className="billing-meter" aria-hidden="true">
                                            <div className="billing-meter-sub" style={meterSegment(balance.subscriptionCoins)} />
                                            <div className="billing-meter-pre" style={meterSegment(balance.prepaidCoins)} />
                                            <div className="billing-meter-post" style={meterSegment(balance.postpaidCoins)} />
                                        </div>
                                        <div className="billing-split">
                                            <span><span className="billing-dot billing-dot-sub" />{t('billing:balance.subCoins', { n: formatNumber(balance.subscriptionCoins) })}</span>
                                            <span><span className="billing-dot billing-dot-pre" />{t('billing:balance.prepaidCoins', { n: formatNumber(balance.prepaidCoins) })}</span>
                                        </div>
                                    </>
                                ) : (
                                    <span className="text-secondary">
                                        {loadFailed.wallet ? t('billing:balance.loadFailed') : t('billing:balance.loading')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="billing-card p-4 h-100 d-flex flex-column">
                        <div className="billing-label mb-2">{t('billing:subscription.label')}</div>
                        {subscription ? (
                            <>
                                <div className="mb-1">
                                    <span className="fw-bold text-capitalize fs-5">{subscription.plan || subscription.plan_id}</span>
                                    <span className="text-secondary small ms-2">({subscription.provider})</span>
                                    {subscription.cancel_at_period_end && <span className="badge bg-warning text-dark ms-2">{t('billing:subscription.cancelledBadge')}</span>}
                                </div>
                                <div className="text-secondary small mb-3">
                                    {t('billing:subscription.status', { status: subscription.status })}
                                    {subscription.current_period_end && <> · {t('billing:subscription.until', { date: formatDate(subscription.current_period_end) })}</>}
                                    {!subscription.cancel_at_period_end && subscription.interval && (
                                        <> · {subscription.interval === 'yearly' ? t('billing:subscription.renewsYearly') : t('billing:subscription.renewsMonthly')}</>
                                    )}
                                </div>
                                {!subscription.cancel_at_period_end && (
                                    <button className="btn btn-outline-danger btn-sm mt-auto align-self-start" onClick={cancelSubscription} disabled={cancelling}>
                                        {cancelling ? t('billing:subscription.cancelling') : t('billing:subscription.cancel')}
                                    </button>
                                )}
                            </>
                        ) : (
                            <span className="text-secondary small">
                                {t('billing:subscription.none')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Abo-Pläne */}
            <div className="billing-section-title">{t('billing:plans.title')}</div>
            {plans ? (
                <div className="row g-3">
                    {plans.subscription_plans.map(plan => {
                        const monthly = priceView(plan.amount_monthly_eur, plan.gross_amount_monthly_eur, plan.vat_rate, plan.vat_applicable);
                        const yearly = priceView(plan.amount_yearly_eur, plan.gross_amount_yearly_eur, plan.vat_rate, plan.vat_applicable);
                        return (
                        <div className="col-12 col-md-4" key={plan.id}>
                            <div className="plan-card">
                                <div className="plan-name">{plan.name}</div>
                                <div className="plan-price">
                                    {formatEur(monthly.gross)}<span className="plan-price-period">{t('billing:plans.perMonth')}</span>
                                </div>
                                <div className="plan-meta">{t('billing:plans.orPerYear', { price: formatEur(yearly.gross), vat: vatSuffix(monthly, tr) })}</div>
                                <div className="plan-coins">
                                    <img src={sengiCoin} alt="" />
                                    {t('billing:plans.creditsPerMonth', { n: formatNumber(plan.coins) })}
                                </div>
                                <div className="plan-meta">{plan.description}</div>
                                <button
                                    className="btn btn-sengi w-100 mt-auto"
                                    onClick={() => setSelection({ kind: 'subscription', plan })}
                                    disabled={!!subscription && !subscription.cancel_at_period_end}
                                    title={subscription && !subscription.cancel_at_period_end ? t('billing:plans.alreadyActive') : ''}
                                >
                                    {t('billing:plans.select')}
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            ) : (
                <span className="text-secondary">
                    {loadFailed.plans ? t('billing:plans.loadFailed') : t('billing:plans.loading')}
                </span>
            )}

            {/* Prepaid */}
            <div className="billing-section-title">{t('billing:prepaid.title')}</div>
            {plans ? (
                (() => {
                    // Der Credit-Kurs pro Euro haengt von der aktuellen Abo-Stufe ab
                    // (credits_per_euro-Formel aus dem Katalog, Key "none" ohne Abo).
                    const tier = subscription?.plan && plans.prepaid.credits_per_euro[subscription.plan] !== undefined
                        ? subscription.plan
                        : 'none';
                    const rate = plans.prepaid.credits_per_euro[tier] ?? 0;
                    return (
                        <div className="row g-3">
                            {plans.prepaid.price_points_eur.map(amountEur => {
                                const pp = plans.prepaid.price_points?.find(p => p.net_amount_eur === amountEur);
                                const view = priceView(amountEur, pp?.gross_amount_eur, plans.prepaid.vat_rate, plans.prepaid.vat_applicable);
                                return (
                                <div className="col-12 col-md-4" key={amountEur}>
                                    <div className="plan-card">
                                        <div className="plan-price">
                                            {formatEur(view.gross)}<span className="plan-price-period"> {view.applicable ? t('billing:vat.inclShort') : t('billing:vat.exclShort')}</span>
                                        </div>
                                        <div className="plan-coins">
                                            <img src={sengiCoin} alt="" />
                                            {t('billing:prepaid.credits', { n: formatNumber(amountEur * rate) })}
                                        </div>
                                        {tier !== 'none' && (
                                            <div className="plan-meta">{t('billing:prepaid.bonusRate', { tier })}</div>
                                        )}
                                        <button
                                            className="btn btn-sengi w-100 mt-auto"
                                            onClick={() => setSelection({ kind: 'prepaid', amountEur, credits: amountEur * rate, grossEur: view.gross, vatRate: view.rate, vatApplicable: view.applicable })}
                                        >
                                            {t('billing:prepaid.select')}
                                        </button>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    );
                })()
            ) : (
                <span className="text-secondary">
                    {loadFailed.plans ? t('billing:prepaid.loadFailed') : t('billing:prepaid.loading')}
                </span>
            )}

            {/* Transaktionen */}
            <div className="billing-section-title">{t('billing:transactions.title')}</div>
            <div className="billing-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={transactionsView === 'cumulative'}
                    className={`billing-tab ${transactionsView === 'cumulative' ? 'active' : ''}`}
                    onClick={() => setTransactionsView('cumulative')}
                >
                    {t('billing:transactions.tabCumulative')}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={transactionsView === 'linear'}
                    className={`billing-tab ${transactionsView === 'linear' ? 'active' : ''}`}
                    onClick={() => setTransactionsView('linear')}
                >
                    {t('billing:transactions.tabHistory')}
                </button>
            </div>
            <div className="billing-card overflow-hidden mb-4">
                {transactions === null ? (
                    <div className="p-3 text-secondary small">
                        {loadFailed.transactions ? t('billing:transactions.loadFailed') : t('billing:transactions.loading')}
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="p-3 text-secondary small">
                        {t('billing:transactions.empty')}
                    </div>
                ) : transactionsView === 'linear' ? (
                    <div className="table-responsive">
                        <table className="table billing-table align-middle">
                            <thead>
                                <tr>
                                    <th>{t('billing:transactions.date')}</th>
                                    <th>{t('billing:transactions.type')}</th>
                                    <th>{t('billing:transactions.description')}</th>
                                    <th className="text-end">{t('billing:transactions.amount')}</th>
                                    <th className="text-end">{t('billing:transactions.balance')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(tx => (
                                    <tr key={tx.id}>
                                        <td className="num">{formatDate(tx.createdAt)}</td>
                                        <td>{t(`billing:txType.${tx.type}`, { defaultValue: tx.type })}</td>
                                        <td className="text-secondary">{tx.description || tx.reference}</td>
                                        <td className={`text-end num fw-bold ${tx.delta < 0 ? 'text-danger' : 'text-success'}`}>
                                            {tx.delta > 0 ? '+' : ''}{formatNumber(tx.delta)}
                                        </td>
                                        <td className="text-end num">{formatNumber(tx.balanceAfter)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    (() => {
                        const blocks = buildCumulativeBlocks(transactions, tr);
                        return blocks.length === 0 ? (
                            <div className="p-3 text-secondary small">
                                {t('billing:transactions.emptyCumulative')}
                            </div>
                        ) : (
                            blocks.map(block => (
                                <div className="billing-period" key={block.key}>
                                    <div className="billing-period-table">
                                        {block.kind === 'usage' ? (
                                            <>
                                                <div className="billing-period-label">{block.label}</div>
                                                {block.categories.map(cat => (
                                                    <Fragment key={cat.name}>
                                                        <div className="billing-period-cell-label">
                                                            {cat.name} <span className="text-secondary">({cat.count}×)</span>
                                                        </div>
                                                        <div className="billing-period-cell-amount num">−{formatNumber(cat.total)}</div>
                                                        <div className="billing-period-cell-extra" />
                                                    </Fragment>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <div className="billing-period-label">{block.label}</div>
                                                {block.items.map(item => (
                                                    <Fragment key={item.key}>
                                                        <div className="billing-period-cell-label billing-period-deposit-label">
                                                            {item.label}
                                                        </div>
                                                        <div className="billing-period-cell-amount num text-success">
                                                            +{formatNumber(item.amount)}
                                                        </div>
                                                        <div className="billing-period-cell-extra" />
                                                    </Fragment>
                                                ))}
                                            </>
                                        )}

                                        <div className={`billing-period-divider ${block.kind === 'deposit' ? 'billing-period-divider-dashed' : ''}`} />
                                        <div className="billing-period-cell-label fw-bold">{t('billing:transactions.total')}</div>
                                        <div className={`billing-period-cell-amount num fw-bold ${block.kind === 'deposit' ? 'text-success' : ''}`}>
                                            {block.kind === 'deposit' ? '+' : '−'}{formatNumber(block.totalSum)}
                                        </div>
                                        <div className="billing-period-cell-extra num">
                                            {block.endingBalance !== null && t('billing:transactions.balanceLabel', { n: formatNumber(block.endingBalance) })}
                                        </div>
                                    </div>
                                </div>
                            ))
                        );
                    })()
                )}
            </div>

            {selection && (
                <CheckoutConfirm
                    selection={selection}
                    onCancel={() => setSelection(null)}
                    onError={text => { setSelection(null); setMessage({ kind: 'danger', text }); }}
                />
            )}
        </div>
    );
};
